/**
 * Mail the console receives itself, on its own SMTP port: invoices mailed to
 * a company's intake address (`<slug>@<INBOUND_DOMAIN>`) and replies to
 * outreach. One row per message per company, whatever happened to it.
 */
import { z } from 'zod';

/** `invoice` mail to the intake address; `reply`/`auto_reply` an answer to outreach. */
export const INBOUND_MAIL_KINDS = ['invoice', 'reply', 'auto_reply'] as const;
export type InboundMailKind = (typeof INBOUND_MAIL_KINDS)[number];

/**
 * `processed`      its PDFs were archived (and, for an allowed sender with a ledger, booked); a reply: recorded and forwarded
 * `duplicate`      the same message (Message-ID) came in before, or every PDF in it is already on file
 * `no_attachment`  no PDF in it: nothing to archive, kept for a person to look at
 * `failed`         a PDF could not be archived
 * `held`           from a sender not on the allowlist (or one that could not be verified): waits for approve or block
 * `blocked`        from a blocked sender: kept for the record, nothing processed
 */
export const INBOUND_MAIL_STATUSES = ['processed', 'duplicate', 'no_attachment', 'failed', 'held', 'blocked'] as const;
export type InboundMailStatus = (typeof INBOUND_MAIL_STATUSES)[number];

/** What happened to one attachment of an invoice mail. */
export interface InboundAttachmentResult {
  filename: string;
  /**
   * `booked` archived and booked in e-Boekhouden; `archived` new and pending review; `duplicate` archived but flagged;
   * `waiting` held with its mail; `skipped` not a PDF; `failed` not archived.
   */
  outcome: 'booked' | 'archived' | 'duplicate' | 'waiting' | 'skipped' | 'failed';
  purchaseInvoiceId: string | null;
  detail: string | null;
}

/**
 * Whether the From address can be trusted: SPF, DKIM and DMARC as checked on
 * arrival. `verified` is a DMARC pass, a DKIM signature aligned with the From
 * domain, or an SPF pass for the From domain. Only a verified mail from an
 * allowed sender is processed without a person.
 */
export interface InboundAuth {
  verified: boolean;
  spf: string | null; dkim: string | null; dmarc: string | null;
  /** The domain that vouched for it (DKIM d=, SPF or DMARC domain), when one did. */
  domain: string | null;
}

export interface InboundMail {
  id: string; kind: InboundMailKind; status: InboundMailStatus;
  auth: InboundAuth | null;
  /** The allowlist or blocklist rule the sender matched. */
  senderRuleId: string | null;
  fromAddress: string; fromName: string | null; toAddress: string; subject: string;
  receivedAt: string; detail: string | null;
  attachments: InboundAttachmentResult[];
  /** Replies only: who answered, and the text of the answer. */
  personId: string | null; personName: string | null; accountId: string | null; accountName: string | null;
  bodyText: string | null;
  /** Replies only: where the copy went, when it was forwarded. */
  forwardedTo: string | null;
}

export const inboundMailListQuery = z.object({
  kind: z.enum(INBOUND_MAIL_KINDS).optional(),
  status: z.enum(INBOUND_MAIL_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

/** The intake address and whether the console takes mail at all (`GET .../inbound`). */
export interface InboundStatus { enabled: boolean; address: string | null }

// ---- senders: the allowlist and blocklist of the intake address -------------------------------

export const INBOUND_SENDER_STATUSES = ['allowed', 'blocked'] as const;
export type InboundSenderStatus = (typeof INBOUND_SENDER_STATUSES)[number];

const domainRe = /^(?=.{1,253}$)([a-z0-9-]+\.)+[a-z]{2,}$/;
/** An address (`facturen@kpn.nl`) or a whole domain (`kpn.nl`, or `@kpn.nl`). */
export const inboundSenderPattern = z.string().trim().toLowerCase().max(254)
  .transform((v) => v.replace(/^@/, ''))
  .refine((v) => (v.includes('@') ? z.string().email().safeParse(v).success : domainRe.test(v)), 'Give an e-mail address or a domain name');

/**
 * One rule. `allowed`: mail from it is processed without a person — every PDF
 * archived and, when `ebLedgerId` is set, booked in e-Boekhouden on that
 * ledger, with `ebRelationId` as the supplier. `blocked`: mail from it is kept
 * for the record and nothing else.
 */
export const inboundSenderSchema = z.object({
  pattern: inboundSenderPattern,
  status: z.enum(INBOUND_SENDER_STATUSES),
  ebRelationId: z.number().int().positive().nullable().default(null),
  ebLedgerId: z.number().int().positive().nullable().default(null),
  note: z.string().trim().max(300).nullable().default(null),
});
export type InboundSenderDto = z.input<typeof inboundSenderSchema>;
export const updateInboundSenderSchema = z.object({
  status: z.enum(INBOUND_SENDER_STATUSES).optional(),
  ebRelationId: z.number().int().positive().nullable().optional(),
  ebLedgerId: z.number().int().positive().nullable().optional(),
  note: z.string().trim().max(300).nullable().optional(),
});
export type UpdateInboundSenderDto = z.input<typeof updateInboundSenderSchema>;

export interface InboundSender {
  id: string; pattern: string; kind: 'address' | 'domain'; status: InboundSenderStatus;
  ebRelationId: number | null; ebLedgerId: number | null; note: string | null;
  /** Mails that matched it, and the last one. */
  mails: number; lastMailAt: string | null;
  createdAt: string; updatedAt: string;
}

/** What saving a rule did to the mails already waiting. */
export interface InboundSenderResult { sender: InboundSender; applied: InboundMail[] }

/** Held mail, grouped by who sent it: what the approve-or-block list shows. */
export interface InboundPendingSender {
  address: string; domain: string; name: string | null;
  count: number; firstAt: string; lastAt: string;
  /** Every one of them passed SPF/DKIM/DMARC. */
  verified: boolean;
  /** Set when an allowlisted sender's mail is held because it could not be verified. */
  allowedButUnverified: boolean;
  mails: InboundMail[];
}
